'use client';

import { FC, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { UserToggles } from '@ravago/shared/page-data/models/config';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { useTranslations } from '@ravago/shared/translations/client';

import { useSearchParams } from 'next/navigation';

import { usePathLocale } from '../../../../../hooks/use-path-locale';
import { LoginButton } from '../../../../shared/login-button/login-button';

interface Props {
   userToggles: UserToggles;
   disabled: boolean;
   onClick: () => void;
}

export const RfqButton: FC<Props> = ({ userToggles, disabled, onClick }) => {
   const translate = useTranslations();
   const { user, canAuthenticate } = useAuthentication();
   const locale = usePathLocale();
   const searchParams = useSearchParams();

   const [requestButtonLabel, setRequestButtonLabel] = useState(
      searchParams.get('flyout') === 'open' ? 'request-for-quotation.continue-quote-request' : 'request-for-quotation.start-quote-request'
   );

   const onStartQuoteClick = () => {
      onClick();
      setRequestButtonLabel('request-for-quotation.continue-quote-request');
   };

   if (!user && !userToggles.hasAnonymousToggle && canAuthenticate) {
      return (
         <LoginButton
            data-testid="rfq-button__login"
            trailingIcon="long-arrow-right"
            fullwidth
            buttonLabel={translate('request-for-quotation.login-to-request')}
         />
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformPendingVerification) && !userToggles.hasPendingToggle) {
      return (
         <Button data-testid="rfq-button__pending" fullWidth trailingIcon="clock" variant="secondary" disabled>
            {translate('request-for-quotation.waiting-verification')}
         </Button>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformGuest) && !userToggles.hasGuestToggle) {
      return (
         <Link underline="none" href={`/${locale}/contact`}>
            <Button data-testid="rfq-button__guest" fullWidth trailingIcon="long-arrow-right" variant="secondary">
               {translate('request-for-quotation.contact-us')}
            </Button>
         </Link>
      );
   }

   return (
      <Button
         data-testid="rfq-button__start"
         fullWidth
         trailingIcon="long-arrow-right"
         disabled={disabled}
         variant="primary"
         onClick={onStartQuoteClick}
         id="start-request-quote-btn"
      >
         {translate(requestButtonLabel)}
      </Button>
   );
};
